import React, { useState } from 'react';
import clsx from 'clsx';
import { BookOpen, ExternalLink, RefreshCw } from 'lucide-react';
import { Badge } from '../../../ui/Badge';
import { Drawer } from '../../../ui/Drawer';

/**
 * "Where did this answer come from?" — per bot reply, the knowledge passages it was given and a
 * check of whether each sentence is backed by them. Data is `message.metadata.sources`, attached by
 * core-ms for signed-in admins only (shared/source-attribution); its shape mirrors StoredSources there.
 */

type Verdict = 'grounded' | 'partial' | 'ungrounded' | 'no-sources' | 'not-applicable';

interface Passage {
  n: number;
  kind: 'document' | 'trained-answer';
  docId: string | null;
  title: string | null;
  url: string | null;
  chunk: string | null;
  page: number | null;
  score: number | null;
  inPrompt: boolean;
  excerpt: string;
}

interface SentenceCheck {
  text: string;
  status: 'supported' | 'partial' | 'unsupported' | 'no-claim';
  source: number | 'caller' | null;
  unverifiedDetails: string[];
}

interface Attribution {
  status: 'pending' | 'done' | 'failed';
  verdict?: Verdict;
  sentences?: SentenceCheck[];
  usedPassages?: number[];
}

export type MessageSources =
  | { v: number; retrieval: false }
  | {
      v: number;
      retrieval: true;
      question: string;
      canAnswer?: boolean;
      passages: Passage[];
      usedCallerInfo: boolean;
      attribution?: Attribution;
    };

const VERDICT_LABEL: Record<Verdict, string> = {
  grounded: 'From knowledge base',
  partial: 'Partly from knowledge base',
  ungrounded: 'Not backed by sources',
  'no-sources': 'No sources found',
  'not-applicable': 'Nothing to verify',
};

const VERDICT_TONE: Record<Verdict, string> = {
  grounded: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  ungrounded: 'bg-red-100 text-red-800',
  'no-sources': 'bg-red-100 text-red-800',
  'not-applicable': 'bg-agentic-gray-100 text-agentic-text-secondary',
};

const VERDICT_HELP: Record<Verdict, string> = {
  grounded: 'Every factual statement is stated in a retrieved passage, and every number, email, phone number and link appears in a source.',
  partial: 'Some statements are in the retrieved passages; others add or change something, or some details could not be found in any source.',
  ungrounded: 'Most of this reply does not match anything that was retrieved — it is likely made up.',
  'no-sources': 'Nothing was found in the knowledge base for this question, so the reply cannot have come from it.',
  'not-applicable': 'The reply has no factual statements to check (for example a greeting or a question back).',
};

function verdictOf(sources: Extract<MessageSources, { retrieval: true }>): Verdict | 'pending' | 'failed' | 'unchecked' {
  const a = sources.attribution;
  if (!a) return sources.passages.length ? 'unchecked' : 'no-sources';
  if (a.status === 'done' && a.verdict) return a.verdict;
  return a.status === 'failed' ? 'failed' : 'pending';
}

/** Small chip under a bot reply; opens the sources panel. Renders nothing for messages without data. */
export const SourcesBadge: React.FC<{ sources?: MessageSources; onOpen: () => void }> = ({ sources, onOpen }) => {
  if (!sources) return null;
  if (!sources.retrieval) {
    return (
      <span className="text-[11px] text-agentic-text-secondary" title="This reply did not use the knowledge base (greeting, booking step, data collection, …).">
        No knowledge lookup
      </span>
    );
  }
  const v = verdictOf(sources);
  const label =
    v === 'pending' ? 'Checking sources…' : v === 'failed' ? 'Source check failed' : v === 'unchecked' ? `${sources.passages.length} sources` : VERDICT_LABEL[v];
  const tone = v === 'pending' || v === 'failed' || v === 'unchecked' ? 'bg-agentic-gray-100 text-agentic-text-primary' : VERDICT_TONE[v];
  return (
    <button type="button" onClick={onOpen} className="inline-flex" title="See which knowledge this reply came from">
      <Badge className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium hover:opacity-80', tone)}>
        <BookOpen size={12} />
        {label}
      </Badge>
    </button>
  );
};

function passageLocation(p: Passage): string {
  if (p.page != null) return `Page ${p.page}`;
  return p.chunk != null ? `Part ${Number(p.chunk) + 1}` : '';
}

/** Chunks ingested before file names were stored carry the document id as their title. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PassageCard: React.FC<{ p: Passage; used: boolean }> = ({ p, used }) => {
  const [expanded, setExpanded] = useState(false);
  const title =
    p.kind === 'trained-answer'
      ? 'Trained answer'
      : p.title && !UUID.test(p.title)
        ? p.title
        : 'Document (re-process it to show its name)';
  const link = p.url;
  return (
    <div className={clsx('rounded-lg border p-3 text-sm', used ? 'border-green-300 bg-green-50' : 'border-agentic-gray-200 bg-white')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-agentic-text-primary">
            <span className="shrink-0 rounded bg-agentic-gray-100 px-1.5 text-[11px]">#{p.n}</span>
            <span className="truncate" title={title}>{title}</span>
            {link && (
              <a href={link} target="_blank" rel="noreferrer" className="shrink-0 text-agentic-primary-blue" title="Open source">
                <ExternalLink size={12} />
              </a>
            )}
          </div>
          {passageLocation(p) && <div className="mt-0.5 text-xs text-agentic-text-secondary">{passageLocation(p)}</div>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 text-[11px]">
          {used && <span className="rounded-full bg-green-100 px-2 text-green-800">Used</span>}
          {!p.inPrompt && (
            <span className="rounded-full bg-agentic-gray-100 px-2 text-agentic-text-secondary" title="Retrieved, but cut by the prompt size limit — the bot never saw it.">
              Not shown to bot
            </span>
          )}
        </div>
      </div>
      <p className={clsx('mt-2 whitespace-pre-wrap text-xs text-agentic-text-secondary', !expanded && 'line-clamp-3')}>{p.excerpt}</p>
      {p.excerpt.length > 160 && (
        <button type="button" className="mt-1 text-[11px] text-agentic-primary-blue" onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const SENTENCE_STYLE: Record<SentenceCheck['status'], string> = {
  supported: 'border-green-400',
  partial: 'border-amber-400',
  unsupported: 'border-red-400 bg-red-50',
  'no-claim': 'border-agentic-gray-200 text-agentic-text-secondary',
};

function sentenceNote(s: SentenceCheck): string {
  if (s.status === 'no-claim') return 'No factual claim — not counted';
  if (s.status === 'unsupported') return 'Not found in any source';
  if (s.source === 'caller') return 'Repeats what the user said';
  if (s.status === 'partial') return s.source != null ? `Partly in source #${s.source} — adds or changes something` : 'Partly supported — adds or changes something';
  return s.source != null ? `Matches source #${s.source}` : 'Supported';
}

/** Right-side panel with the verdict, the reply checked sentence by sentence, and the passages. */
export const SourcesDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  sources?: MessageSources;
  onRefresh?: () => void;
}> = ({ open, onClose, sources, onRefresh }) => {
  const [showUnused, setShowUnused] = useState(false);
  if (!sources || !sources.retrieval) return null;

  const v = verdictOf(sources);
  const used = new Set(sources.attribution?.usedPassages ?? []);
  const sentences = sources.attribution?.sentences ?? [];
  const usedPassages = sources.passages.filter((p) => used.has(p.n));
  const otherPassages = sources.passages.filter((p) => !used.has(p.n));
  const checked = sources.attribution?.status === 'done';

  return (
    <Drawer open={open} onClose={onClose} title="Response sources" widthClassName="w-full max-w-lg">
      <div className="space-y-5 text-sm">
        <section>
          {v === 'pending' ? (
            <div className="flex items-center justify-between rounded-lg bg-agentic-gray-100 p-3">
              <span>Checking this reply against its sources…</span>
              {onRefresh && (
                <button type="button" onClick={onRefresh} className="inline-flex items-center gap-1 text-agentic-primary-blue">
                  <RefreshCw size={14} /> Refresh
                </button>
              )}
            </div>
          ) : v === 'failed' || v === 'unchecked' ? (
            <div className="rounded-lg bg-agentic-gray-100 p-3">
              {v === 'failed' ? 'The automatic source check failed for this reply. The retrieved passages are listed below.' : 'This reply was not checked automatically. The retrieved passages are listed below.'}
            </div>
          ) : (
            <div className={clsx('rounded-lg p-3', VERDICT_TONE[v])}>
              <div className="font-semibold">{VERDICT_LABEL[v]}</div>
              <div className="mt-1 text-xs">{VERDICT_HELP[v]}</div>
            </div>
          )}
          <div className="mt-2 text-xs text-agentic-text-secondary">
            Question searched: “{sources.question}”
            {sources.canAnswer === false && ' · The bot judged that the retrieved knowledge could not answer it.'}
            {sources.usedCallerInfo && ' · Details the user gave earlier were also available.'}
          </div>
        </section>

        {checked && sentences.length > 0 && (
          <section>
            <h4 className="mb-2 font-semibold text-agentic-text-primary">Reply, sentence by sentence</h4>
            <ul className="space-y-1.5">
              {sentences.map((s, i) => (
                <li key={i} className={clsx('border-l-4 py-1 pl-3 pr-2', SENTENCE_STYLE[s.status])}>
                  <div>{s.text}</div>
                  <div className="mt-0.5 text-[11px] text-agentic-text-secondary">{sentenceNote(s)}</div>
                  {s.unverifiedDetails.length > 0 && (
                    <div className="mt-0.5 text-[11px] font-medium text-red-700">
                      Not in any source: {s.unverifiedDetails.join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h4 className="mb-2 font-semibold text-agentic-text-primary">
            {checked ? `Sources used (${usedPassages.length})` : `Retrieved passages (${sources.passages.length})`}
          </h4>
          {sources.passages.length === 0 ? (
            <p className="text-agentic-text-secondary">Nothing was retrieved from the knowledge base for this question.</p>
          ) : checked ? (
            <div className="space-y-2">
              {usedPassages.length === 0 && <p className="text-agentic-text-secondary">No retrieved passage backs this reply.</p>}
              {usedPassages.map((p) => <PassageCard key={p.n} p={p} used />)}
              {otherPassages.length > 0 && (
                <>
                  <button type="button" className="text-xs text-agentic-primary-blue" onClick={() => setShowUnused((s) => !s)}>
                    {showUnused ? 'Hide' : 'Show'} {otherPassages.length} other retrieved passage{otherPassages.length === 1 ? '' : 's'}
                  </button>
                  {showUnused && otherPassages.map((p) => <PassageCard key={p.n} p={p} used={false} />)}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">{sources.passages.map((p) => <PassageCard key={p.n} p={p} used={false} />)}</div>
          )}
        </section>
      </div>
    </Drawer>
  );
};
